import React, { useEffect, useRef, useState } from "react";
import LoadingSpinner from "@/components/LoadingSpinner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useModelConfigsDataList } from "@/queries/models";
import { Chart, registerables } from "chart.js";
import { InfoIcon } from "lucide-react";
import { useAuth } from "@/context/auth-context";

Chart.register(...registerables);

type ModelParametersHelpModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

interface ModelWithPricing {
  provider: string;
  name: string;
  input_price: number;
  output_price: number;
}

/**
 * This component displays a modal with various charts (scatter and bar)
 * comparing the pricing of different LLM models (input/output costs).
 * Users can toggle between logarithmic and linear scales for the charts.
 */
export const ModelParametersInfoModal = ({
  open,
  onOpenChange,
}: ModelParametersHelpModalProps) => {
  const auth = useAuth();
  const userToken = auth!.userToken!;
  const { data, isLoading } = useModelConfigsDataList(userToken);

  // Maintain list of models that pass our filter criteria (pricing, providers)
  const [filteredModels, setFilteredModels] = useState<ModelWithPricing[]>([]);
  const [ignoredProviders] = useState([
    "anthropic.bedrock",
    "huggingface",
    "openai.azure",
    "cohere",
    "mistral",
  ]);
  const [preferredOrder] = useState(["openai", "anthropic", "google"]);

  // Refs for charts
  const scatterRef = useRef<HTMLCanvasElement>(null);
  const inputRef = useRef<HTMLCanvasElement>(null);
  const outputRef = useRef<HTMLCanvasElement>(null);

  // Chart instances to destroy/rebuild on changes
  const scatterChartRef = useRef<Chart | null>(null);
  const inputChartRef = useRef<Chart | null>(null);
  const outputChartRef = useRef<Chart | null>(null);

  // Scale toggle (either "logarithmic" or "linear")
  const [scaleType, setScaleType] = useState<"logarithmic" | "linear">(
    "linear",
  );

  // Dynamic chart heights
  const [inputChartHeight, setInputChartHeight] = useState(400);
  const [outputChartHeight, setOutputChartHeight] = useState(400);

  // Helper function to map provider names to chart colors
  const getProviderColor = (providerName: string) => {
    const colors: Record<string, string> = {
      openai: "rgba(52, 53, 65, 1)",
      anthropic: "rgba(213, 107, 79, 1)",
      google: "rgba(49, 122, 231, 1)",
      mistral: "rgba(128, 0, 128, 1)",
      cohere: "rgba(220, 53, 69, 1)",
      "anthropic.bedrock": "rgba(255, 153, 51, 1)",
      "openai.azure": "rgba(0, 204, 153, 1)",
      huggingface: "rgba(153, 102, 255, 1)",
    };
    return colors[providerName] || "rgba(100, 100, 100, 1)";
  };

  // Filter out ignored providers and gather all model pricing
  useEffect(() => {
    if (!data?.results || !data.results.length) return;

    const modelConfigs = data.results;

    // Filter out providers
    const filteredProviders = modelConfigs
      .filter((p: any) => !ignoredProviders.includes(p.provider_name))
      .sort((a: any, b: any) => {
        const indexA = preferredOrder.indexOf(a.provider_name);
        const indexB = preferredOrder.indexOf(b.provider_name);
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        return a.provider_name.localeCompare(b.provider_name);
      });

    // Collect all models with valid pricing
    const allModelsWithPricing: ModelWithPricing[] = [];
    filteredProviders.forEach((provider: any) => {
      provider.model_configs.forEach((m: any) => {
        if (
          m.price_per_1m_tokens_input !== undefined &&
          m.price_per_1m_tokens_output !== undefined
        ) {
          allModelsWithPricing.push({
            provider: provider.provider_name,
            name: m.display_name || m.llm_model_name,
            input_price: m.price_per_1m_tokens_input,
            output_price: m.price_per_1m_tokens_output,
          });
        }
      });
    });

    setFilteredModels(allModelsWithPricing);
  }, [data, ignoredProviders, preferredOrder]);

  // Build or rebuild charts whenever the modal is opened, the data changes, or the scale changes
  useEffect(() => {
    if (!open) return;

    // Wait a frame so canvases are rendered and sized before chart creation
    requestAnimationFrame(() => {
      scatterChartRef.current?.destroy();
      inputChartRef.current?.destroy();
      outputChartRef.current?.destroy();

      if (!filteredModels.length) return;

      // ---- Scatter Chart Setup ----
      const scatterDataByProvider: Record<string, any[]> = {};
      filteredModels.forEach((m) => {
        if (!scatterDataByProvider[m.provider]) {
          scatterDataByProvider[m.provider] = [];
        }
        scatterDataByProvider[m.provider].push({
          x: m.output_price,
          y: m.input_price,
          name: m.name,
        });
      });
      const scatterDatasets = Object.keys(scatterDataByProvider).map(
        (prov) => ({
          label: prov,
          data: scatterDataByProvider[prov],
          backgroundColor: getProviderColor(prov),
          borderColor: getProviderColor(prov),
          pointRadius: 6,
        }),
      );

      const scatterOpts = {
        type: "scatter" as const,
        data: { datasets: scatterDatasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              type: scaleType,
              title: { display: true, text: "Output Price per 1M tokens ($)" },
            },
            y: {
              type: scaleType,
              title: { display: true, text: "Input Price per 1M tokens ($)" },
            },
          },
          plugins: {
            tooltip: {
              mode: "nearest" as "nearest",
              intersect: false,
              callbacks: {
                title: () => "", // Empty title
                label: (context: any) => {
                  const item = context.raw;
                  return [
                    `🤖 ${item.name}`,
                    `${context.dataset.label.toUpperCase()}`,
                    `Input: $${item.y.toFixed(2)} | Output: $${item.x.toFixed(
                      2,
                    )}`,
                    "", // Add empty line between models
                  ];
                },
              },
              displayColors: false,
              enabled: true,
              position: "nearest" as const,
              backgroundColor: "rgba(0,0,0,0.8)",
              bodyFont: { size: 13 },
              padding: 10,
              cornerRadius: 4,
            },
            legend: { display: true },
            datalabels: { display: false },
          },
        },
      };

      // ---- Input Bar Chart Setup ----
      // Explanation: Multiply the number of models by 30 to get rough height, add 50 for padding
      const sortedInput = [...filteredModels]
        .filter((m) => m.input_price > 0)
        .sort((a, b) => b.input_price - a.input_price);
      const calculatedInputHeight = sortedInput.length * 30 + 50;
      setInputChartHeight(Math.max(calculatedInputHeight, 400));

      const inputBarOpts = {
        type: "bar" as const,
        data: {
          labels: sortedInput.map((m) => m.name),
          datasets: [
            {
              label: "Input Price ($) per 1M tokens",
              data: sortedInput.map((m) => m.input_price),
              backgroundColor: sortedInput.map((m) =>
                getProviderColor(m.provider),
              ),
            },
          ],
        },
        options: {
          indexAxis: "y" as const,
          maintainAspectRatio: false,
          scales: {
            x: {
              type: scaleType,
              position: "top" as const,
              title: { display: true, text: "Price per 1M tokens ($)" },
            },
            y: { title: { display: true, text: "Model" } },
          },
          plugins: {
            tooltip: {
              callbacks: {
                label: (context: any) => {
                  const model = sortedInput[context.dataIndex];
                  return [
                    `${model.provider.toUpperCase()}: $${model.input_price.toFixed(
                      2,
                    )} per 1M tokens`,
                  ];
                },
              },
            },
            datalabels: { display: false },
          },
        },
      };

      // ---- Output Bar Chart Setup ----
      const sortedOutput = [...filteredModels]
        .filter((m) => m.output_price > 0)
        .sort((a, b) => b.output_price - a.output_price);
      const calculatedOutputHeight = sortedOutput.length * 30 + 50;
      setOutputChartHeight(Math.max(calculatedOutputHeight, 400));

      const outputBarOpts = {
        type: "bar" as const,
        data: {
          labels: sortedOutput.map((m) => m.name),
          datasets: [
            {
              label: "Output Price ($) per 1M tokens",
              data: sortedOutput.map((m) => m.output_price),
              backgroundColor: sortedOutput.map((m) =>
                getProviderColor(m.provider),
              ),
            },
          ],
        },
        options: {
          indexAxis: "y" as const,
          maintainAspectRatio: false,
          scales: {
            x: {
              type: scaleType,
              position: "top" as const,
              title: { display: true, text: "Price per 1M tokens ($)" },
            },
            y: { title: { display: true, text: "Model" } },
          },
          plugins: {
            tooltip: {
              callbacks: {
                label: (context: any) => {
                  const model = sortedOutput[context.dataIndex];
                  return [
                    `${model.provider.toUpperCase()}: $${model.output_price.toFixed(
                      2,
                    )} per 1M tokens`,
                  ];
                },
              },
            },
            datalabels: { display: false },
          },
        },
      };

      // Create the new charts
      if (scatterRef.current) {
        scatterChartRef.current = new Chart(scatterRef.current, scatterOpts);
      }
      if (inputRef.current) {
        inputChartRef.current = new Chart(inputRef.current, inputBarOpts);
      }
      if (outputRef.current) {
        outputChartRef.current = new Chart(outputRef.current, outputBarOpts);
      }
    });
  }, [open, filteredModels, scaleType]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl overflow-hidden">
        <DialogHeader className="border-b pb-4">
          <DialogTitle className="text-2xl font-semibold">
            Model Pricing
          </DialogTitle>
          <DialogDescription className="mt-1 text-base text-gray-500">
            Pricing comparison across different LLM providers and models.
            <div className="mt-2 flex items-center rounded-md border border-gray-200 bg-gray-100 p-2 text-sm text-gray-700">
              <InfoIcon
                className="mr-2 h-5 w-5 text-gray-500"
                stroke="currentColor"
                fill="none"
                strokeWidth={2}
                aria-hidden="true"
              />
              <span>
                Prices are calculated based on 1M tokens (≈ 750,000 words,
                roughly the length of "Atlas Shrugged")
              </span>
            </div>
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex h-48 items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="h-[700px] overflow-y-auto pr-2">
            {/* Charts and Model Pricing Section */}
            <div>
              <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-800">
                    Input vs Output Pricing
                  </h2>
                  <div className="flex items-center space-x-3">
                    <span className="text-sm font-medium text-gray-600">
                      Scale:
                    </span>
                    <div className="relative">
                      <select
                        id="scaleToggle"
                        className="appearance-none rounded-md border border-gray-300 bg-white py-1.5 pl-3 pr-8 text-sm font-medium text-gray-700 shadow-sm hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        value={scaleType}
                        onChange={(e) =>
                          setScaleType(
                            e.target.value as "linear" | "logarithmic",
                          )
                        }
                      >
                        <option value="logarithmic">Logarithmic</option>
                        <option value="linear">Linear</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="relative h-[550px]">
                  <canvas ref={scatterRef} />
                </div>
              </div>

              {/* Input and Output Pricing Bar Charts */}
              <div className="flex flex-col space-y-8">
                <div className="flex w-full space-x-6">
                  <div className="flex-1">
                    <div className="mb-3 flex items-center px-1">
                      <h3 className="text-lg font-semibold text-gray-800">
                        Input Pricing
                      </h3>
                      <span className="ml-2 text-sm text-gray-500">
                        (per 1M tokens)
                      </span>
                    </div>
                    <div
                      className="relative rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
                      style={{ height: `${inputChartHeight}px` }}
                    >
                      <canvas ref={inputRef} />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="mb-3 flex items-center px-1">
                      <h3 className="text-lg font-semibold text-gray-800">
                        Output Pricing
                      </h3>
                      <span className="ml-2 text-sm text-gray-500">
                        (per 1M tokens)
                      </span>
                    </div>
                    <div
                      className="relative rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
                      style={{ height: `${outputChartHeight}px` }}
                    >
                      <canvas ref={outputRef} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-6">
              <Button onClick={() => onOpenChange(false)}>Close</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
